// Every word camera help shows (src/ui/CameraHelp.tsx, src/ui/SkyView.tsx), in one place to read and approve. It helps
// rather than decides: phone video catches only the brightest stars, so nothing here says what the person can or cannot
// see, only what the camera finds and what seems to be in its way.

/**
 * Camera help is withdrawn until the owner settles how it should work (23 October 2026, DECISIONS 110): its switch, its
 * chip and its note are not shown, and the camera is never opened for it, even where it was switched on. Its code stays,
 * ready to come back.
 */
export const CAMERA_HELP_OFFERED = false;

export const CAMERA_HELP = {
  /** The switch, in Sky view next to Naked eye. Its last sentence is the privacy line. */
  label: 'Camera help',
  sub: 'On a phone, in pointing mode, the camera rings the bright stars and planets it finds and tells you when cloud seems to hide them. It catches only the brightest stars, so it helps rather than decides. What the camera sees stays on your phone and is never saved.',
  /** Once, the first time it is switched on. */
  tip: 'Rings mark the bright stars and planets the camera finds. Your eyes may see more than it does.',
  /**
   * The chip in pointing mode: off, it switches camera help on; on, beside the camera icon, it says what the camera is
   * doing (a tap switches it off). Screen readers hear "Camera help:" before it.
   */
  chipOff: 'Camera help',
  chip: {
    starting: 'Starting',
    looking: 'Looking',
    waiting: 'Waiting',
    found: (n: number) => `${n} found`,
  },
  /** A short note over the sky. */
  note: {
    cloud: 'Cloud seems to be hiding the bright stars here.',
    haze: 'Haze or thin cloud seems to be dimming the stars here.',
    day: 'Camera help works once it is dark.',
    elsewhere: 'Camera help works with the sky here and now.',
  },
  /** When the camera cannot be used, camera help switches itself off and says why, as far as the browser says. */
  refused: {
    denied: 'Camera help is off: the camera was not allowed.',
    other: 'Camera help is off: the camera could not be started.',
  },
} as const;

/** Why the camera could not be used, from the error the browser gave: not allowed, or anything else (no camera, one in
 *  use elsewhere or failing, no camera on an insecure page: browsers do not tell these apart reliably). */
export function refusal(e: unknown): keyof typeof CAMERA_HELP.refused {
  const name = e && typeof e === 'object' && 'name' in e ? String((e as { name: unknown }).name) : '';
  return name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError' ? 'denied' : 'other';
}
